import React, { forwardRef } from 'react';
import cn from 'classnames';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next/';

import styles from './PersonalDataForm.module.scss';
import Props from './PersonalDataForm.props';
import { REGEXP } from '@constants/index';
import Button from '@components/ui/Button/Button';
import Input from '@components/ui/Input/Input';
import TextLink from '@components/ui/TextLink/TextLink';

const PersonalDataForm = (
  { initialValues, onSubmit, error, message, isLoading, deleteAccountText }: Props,
  ref: any,
) => {
  const { t } = useTranslation();

  const validationScheme = Yup.object().shape({
    email: Yup.string()
      .email(t('VALIDATION.E_MAIL_INVALID'))
      .required(t('VALIDATION.FIELD_REQUIRED')),
    password: Yup.string()
      .required(t('VALIDATION.FIELD_REQUIRED'))
      .matches(REGEXP.STRONG_PASSWORD, t('VALIDATION.WEAK_PASSWORD')),
    passwordVerify: Yup.string()
      .oneOf([Yup.ref('password'), null], t('VALIDATION.PASSWORDS_DONT_MATCH'))
      .required(t('VALIDATION.FIELD_REQUIRED')),
  });

  return (
    <div className={styles.PersonalDataForm}>
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationScheme}
        validateOnBlur={false}
        validateOnChange>
        {() => {
          return (
            <Form>
              <div className="mb-sm">
                <Field name="email">
                  {({ form, field }: FieldProps) => {
                    return (
                      <Input
                        label={t('DOMAIN.AUTHENTICATION.EMAIL')}
                        error={form.touched.email && (form.errors.email as string)}
                        data-testid="personalDataFormEmailInput"
                        type="email"
                        disabled
                        {...field}
                      />
                    );
                  }}
                </Field>
              </div>
              <div className="mb-sm">
                <Field name="password">
                  {({ form, field }: FieldProps) => {
                    return (
                      <Input
                        label={t('DOMAIN.AUTHENTICATION.PASSWORD')}
                        error={form.touched.password && (form.errors.password as string)}
                        type="password"
                        data-testid="personalDataFormPasswordInput"
                        {...field}
                      />
                    );
                  }}
                </Field>
              </div>
              <div className="mb-sm">
                <Field name="passwordVerify">
                  {({ form, field }: FieldProps) => {
                    return (
                      <Input
                        label={t('DOMAIN.AUTHENTICATION.REPEAT_PASSWORD')}
                        error={
                          form.touched.passwordVerify && (form.errors.passwordVerify as string)
                        }
                        type="password"
                        data-testid="personalDataFormPasswordInput"
                        {...field}
                      />
                    );
                  }}
                </Field>
              </div>

              {!!message && (
                <div className={styles.Message} data-testid="personalDataFormMessageContainer">
                  {message}
                </div>
              )}

              {!!error && (
                <div className={styles.Error} data-testid="personalDataFormErrorContainer">
                  {error}
                </div>
              )}
              <div className={styles.FormFooter}>
                <Button
                  isLoading={isLoading}
                  type="submit"
                  data-testid="personalDataFormSubmitButton">
                  {t('DOMAIN.ACCOUNT.SAVE')}
                </Button>
                <div className={styles.DeleteAccountText}>
                  <TextLink className={styles.DeleteAccountLink}>
                    {t('DOMAIN.ACCOUNT.REMOVE')}
                  </TextLink>
                  <p className={cn(styles.DeleteText)}>{deleteAccountText}</p>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default forwardRef(PersonalDataForm);
